import { proposalsService } from '@/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type TransmitProposalInput = {
  policyId: string;
  productCode?: string | null;
};

/**
 * Erro lançado quando a transmissão é bloqueada porque o Insuremo encaminhou
 * a proposta para análise de subscrição (status 18). Não é uma falha real:
 * a proposta foi atualizada e o usuário deve ser avisado, não ver um toast de erro.
 */
export class ProposalSentToAnalysisError extends Error {
  readonly proposalSentToAnalysis = true as const;

  constructor(message?: string) {
    super(message ?? 'A proposta foi encaminhada para análise de subscrição.');
    this.name = 'ProposalSentToAnalysisError';
  }
}

export function isProposalSentToAnalysisError(
  error: unknown
): error is ProposalSentToAnalysisError {
  return error instanceof ProposalSentToAnalysisError;
}

function indicatesSentToAnalysis(message?: string): boolean {
  if (!message) return false;
  // Backend (via Insuremo) responde 400 com detail "A proposta foi encaminhada
  // para análise de subscrição." quando a transmissão dispara o underwriting.
  return message.toLowerCase().includes('encaminhada para análise');
}

export function useTransmitProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ policyId, productCode }: TransmitProposalInput) => {
      const result = await proposalsService.transmitProposal(
        policyId,
        productCode
      );
      if (!result.success) {
        if (indicatesSentToAnalysis(result.error)) {
          throw new ProposalSentToAnalysisError(result.error);
        }
        throw new Error(result.error ?? 'Erro ao transmitir proposta');
      }
      return {
        policyId,
        sentToAnalysis:
          'sentToAnalysis' in result && result.sentToAnalysis === true,
      };
    },
    onSuccess: async (_, { policyId }) => {
      await queryClient.invalidateQueries({ queryKey: ['proposal', policyId] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
    onError: async (error, { policyId }) => {
      if (isProposalSentToAnalysisError(error)) {
        await queryClient.invalidateQueries({
          queryKey: ['proposal', policyId],
        });
        await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      }
    },
  });
}
